import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useI18n } from "./i18n";
import { phoneSendOTP, phoneVerify } from "./client";

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}`;

const Overlay = styled.div`
  position:fixed;inset:0;z-index:500;
  background:rgba(0,0,0,.82);backdrop-filter:blur(8px);
  display:flex;align-items:center;justify-content:center;padding:1rem;
  animation:${fadeIn} .2s;
`;
const Box = styled.div`
  background:${({theme}) => theme.bgCard};
  border:1px solid ${({theme}) => theme.borderLight};
  border-radius:16px;width:100%;max-width:420px;overflow:hidden;
  animation:${slideUp} .3s ease;
`;
const Head = styled.div`
  padding:1.25rem 1.4rem;border-bottom:1px solid ${({theme}) => theme.border};
  display:flex;align-items:center;justify-content:space-between;
`;
const Title = styled.h3`
  font-family:${({theme}) => theme.fontDisplay};
  font-size:1.05rem;font-weight:700;letter-spacing:-.02em;
`;
const Close = styled.button`
  background:none;border:none;color:${({theme}) => theme.textMuted};
  cursor:pointer;font-size:1.1rem;
  &:hover{color:${({theme}) => theme.text};}
`;
const Body = styled.div`padding:1.4rem;display:flex;flex-direction:column;gap:1rem;`;
const Sub = styled.p`
  font-size:.8rem;color:${({theme}) => theme.textDim};line-height:1.55;
`;
const WhatsAppBadge = styled.div`
  display:inline-flex;align-items:center;gap:.5rem;
  padding:.45rem .85rem;border-radius:100px;
  background:rgba(37,211,102,.1);
  border:1px solid rgba(37,211,102,.3);
  color:#25d366;font-size:.72rem;font-weight:600;width:fit-content;
`;
const Field = styled.div`display:flex;flex-direction:column;gap:.3rem;`;
const Label = styled.label`
  font-size:.68rem;text-transform:uppercase;letter-spacing:.08em;
  color:${({theme}) => theme.textMuted};
`;
const Input = styled.input`
  background:${({theme}) => theme.bg};
  border:1px solid ${({theme}) => theme.border};
  border-radius:8px;color:${({theme}) => theme.text};
  font-size:.88rem;padding:.65rem .9rem;outline:none;
  font-family:${({theme}) => theme.fontMono};
  &:focus{border-color:${({theme}) => theme.accent};}
`;
const OtpRow = styled.div`display:flex;gap:.4rem;justify-content:center;`;
const OtpBox = styled.input`
  width:42px;height:52px;text-align:center;
  background:${({theme}) => theme.bg};
  border:1px solid ${({$filled, theme}) => $filled ? theme.accent : theme.border};
  border-radius:8px;color:${({theme}) => theme.text};
  font-family:${({theme}) => theme.fontMono};
  font-size:1.2rem;font-weight:600;outline:none;
  transition:border-color .15s;
  &:focus{border-color:${({theme}) => theme.accent};}
`;
const Btn = styled.button`
  padding:.7rem 1rem;border-radius:8px;border:none;
  background:${({$primary, theme, disabled}) =>
    disabled ? theme.bgHover : $primary ? "#25d366" : theme.bgHover};
  color:${({$primary, disabled, theme}) =>
    disabled ? theme.textMuted : $primary ? "#fff" : theme.text};
  font-family:${({theme}) => theme.fontBody};
  font-size:.82rem;font-weight:700;letter-spacing:.02em;
  cursor:${({disabled}) => disabled ? "not-allowed" : "pointer"};
  transition:opacity .15s;width:100%;
  &:hover:not(:disabled){opacity:.88;}
`;
const Err = styled.div`
  font-size:.75rem;color:#ff3b5c;
  background:rgba(255,59,92,.08);
  border:1px solid rgba(255,59,92,.3);
  border-radius:7px;padding:.55rem .75rem;
`;
const Success = styled.div`
  font-size:.78rem;color:#00e676;
  background:rgba(0,230,118,.08);
  border:1px solid rgba(0,230,118,.3);
  border-radius:7px;padding:.7rem .85rem;text-align:center;
`;
const Resend = styled.button`
  background:none;border:none;
  color:${({theme}) => theme.accent};
  font-size:.72rem;cursor:pointer;text-decoration:underline;
  text-underline-offset:3px;
  &:disabled{opacity:.4;cursor:not-allowed;}
`;

const T = {
  title:        { fr:"Vérifier votre téléphone", en:"Verify your phone", es:"Verifica tu teléfono", de:"Telefon verifizieren", pt:"Verificar seu telefone", ar:"تحقق من هاتفك" },
  sub:          { fr:"Avant de pouvoir trader, vérifiez votre numéro de téléphone via WhatsApp. Cela nous protège contre la fraude et débloque les commissions de parrainage.",
                  en:"Before you can trade, verify your phone via WhatsApp. This protects against fraud and unlocks referral commissions.",
                  es:"Antes de poder operar, verifica tu teléfono vía WhatsApp.",
                  de:"Verifiziere dein Telefon per WhatsApp, bevor du handeln kannst.",
                  pt:"Antes de poder operar, verifique seu telefone via WhatsApp.",
                  ar:"قبل أن تتمكن من التداول، تحقق من هاتفك عبر WhatsApp." },
  phone:        { fr:"Numéro de téléphone", en:"Phone number", es:"Número de teléfono", de:"Telefonnummer", pt:"Número de telefone", ar:"رقم الهاتف" },
  send:         { fr:"Envoyer le code WhatsApp", en:"Send WhatsApp code", es:"Enviar código WhatsApp", de:"WhatsApp-Code senden", pt:"Enviar código WhatsApp", ar:"إرسال رمز WhatsApp" },
  sending:      { fr:"Envoi…", en:"Sending…", es:"Enviando…", de:"Senden…", pt:"Enviando…", ar:"جارٍ الإرسال…" },
  enterCode:    { fr:"Entrez le code reçu", en:"Enter the code received", es:"Ingresa el código", de:"Code eingeben", pt:"Digite o código", ar:"أدخل الرمز" },
  codeSent:     { fr:"Code envoyé à", en:"Code sent to", es:"Código enviado a", de:"Code gesendet an", pt:"Código enviado para", ar:"تم إرسال الرمز إلى" },
  verify:       { fr:"Vérifier", en:"Verify", es:"Verificar", de:"Verifizieren", pt:"Verificar", ar:"تحقق" },
  verifying:    { fr:"Vérification…", en:"Verifying…", es:"Verificando…", de:"Wird überprüft…", pt:"Verificando…", ar:"جارٍ التحقق…" },
  resend:       { fr:"Renvoyer le code", en:"Resend code", es:"Reenviar código", de:"Code erneut senden", pt:"Reenviar código", ar:"إعادة إرسال الرمز" },
  verified:     { fr:"✓ Téléphone vérifié — vous pouvez maintenant trader !",
                  en:"✓ Phone verified — you can now trade!",
                  es:"✓ Teléfono verificado — ya puedes operar.",
                  de:"✓ Telefon verifiziert — du kannst jetzt handeln.",
                  pt:"✓ Telefone verificado — agora pode operar.",
                  ar:"✓ تم التحقق من الهاتف — يمكنك التداول الآن!" },
};
const tr = (k, lang) => T[k]?.[lang] ?? T[k]?.fr ?? k;


export default function PhoneVerificationModal({ open, onClose, onVerified }) {
  const { lang } = useI18n();
  const [step,    setStep]    = useState("phone");   // "phone" | "otp" | "done"
  const [phone,   setPhone]   = useState("+");
  const [otp,     setOtp]     = useState(["","","","","",""]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = [useRef(),useRef(),useRef(),useRef(),useRef(),useRef()];

  // Cooldown pour le renvoi
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSend = async () => {
    setError(""); setLoading(true);
    try {
      const r = await phoneSendOTP(phone);
      setStep("otp");
      setCooldown(60);
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur d'envoi.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...otp]; next[i] = v; setOtp(next);
    if (v && i < 5) otpRefs[i+1].current?.focus();
  };

  const handleOtpKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      otpRefs[i-1].current?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) return;
    setError(""); setLoading(true);
    try {
      await phoneVerify(phone, code);
      setStep("done");
      setTimeout(() => {
        onVerified?.();
        onClose?.();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Code invalide.");
      setOtp(["","","","","",""]);
      otpRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose?.()}>
      <Box>
        <Head>
          <Title>📞 {tr("title", lang)}</Title>
          <Close onClick={onClose}>✕</Close>
        </Head>

        <Body>
          {step === "phone" && (
            <>
              <Sub>{tr("sub", lang)}</Sub>
              <WhatsAppBadge>
                <span style={{fontSize:"1rem"}}>📱</span>
                WhatsApp · gratuit
              </WhatsAppBadge>
              <Field>
                <Label>{tr("phone", lang)}</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+33612345678"
                  autoFocus
                />
              </Field>
              {error && <Err>{error}</Err>}
              <Btn $primary onClick={handleSend} disabled={loading || phone.length < 8}>
                {loading ? tr("sending", lang) : tr("send", lang)}
              </Btn>
            </>
          )}

          {step === "otp" && (
            <>
              <Sub>{tr("codeSent", lang)} <strong style={{color:"#e8edf2"}}>{phone}</strong></Sub>
              <Sub style={{fontSize:".72rem",color:"#5a6a7a"}}>{tr("enterCode", lang)}</Sub>
              <OtpRow>
                {otp.map((v, i) => (
                  <OtpBox
                    key={i}
                    ref={otpRefs[i]}
                    $filled={!!v}
                    maxLength={1}
                    value={v}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKey(i, e)}
                  />
                ))}
              </OtpRow>
              {error && <Err>{error}</Err>}
              <Btn $primary onClick={handleVerify} disabled={loading || otp.join("").length !== 6}>
                {loading ? tr("verifying", lang) : tr("verify", lang)}
              </Btn>
              <div style={{textAlign:"center"}}>
                <Resend
                  onClick={() => { setStep("phone"); setOtp(["","","","","",""]); }}
                  disabled={cooldown > 0}
                >
                  {cooldown > 0 ? `${tr("resend", lang)} (${cooldown}s)` : tr("resend", lang)}
                </Resend>
              </div>
            </>
          )}

          {step === "done" && (
            <Success>{tr("verified", lang)}</Success>
          )}
        </Body>
      </Box>
    </Overlay>
  );
}